import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PIPELINE_STAGES, PIPELINE_STAGE_HINTS, PIPELINE_STAGE_LABELS, formatCents, type PipelineBoard, type PipelineCard, type PipelineStage } from '@kontor/shared';
import { api, queryString } from '../../api';
import './flow.css';

const POLL_MS = 60_000;
const day = (iso: string | null) => (iso ? new Date(iso.length <= 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' }) : '–');

/** Wohin der Eintrag führt und wohin „In der Liste öffnen“ einer Phase */
const cardLink = (c: PipelineCard) => (c.kind === 'inquiry' ? `/anfragen/${c.id}` : c.kind === 'offer' ? `/angebote/${c.id}` : `/auftraege/${c.id}`);
const listLink: Record<PipelineStage, string> = {
  inquiry: '/anfragen',
  offer: '/angebote',
  preparing: '/auftraege?status=open',
  handed_over: '/auftragsplanung',
  planned: '/auftraege?status=planned',
  in_progress: '/auftraege?status=in_progress',
  to_invoice: '/auftraege?status=done',
};
/** Kurze Namen für die Phasenleiste, die langen stehen über der Liste */
const SHORT: Record<PipelineStage, string> = { inquiry: 'Anfragen', offer: 'Angebote', preparing: 'Vorbereitung', handed_over: 'Übergeben', planned: 'Geplant', in_progress: 'Umsetzung', to_invoice: 'Abrechnen' };
const STAGE_COLORS = ['#6366f1', '#0ea5e9', '#14b8a6', '#f59e0b', '#8b5cf6', '#22c55e', '#ef4444'];

/**
 * Auftragsübersicht (KON-172): der ganze Weg von der Anfrage über Angebot und Nachfassen zum verbindlichen Auftrag, zur Übergabe an das Personalmanagement, Planung, Umsetzung und
 * Abrechnung. Oben die sieben Phasen mit Anzahl und Summe, immer in einer Zeile und über alle Einträge gezählt. Darunter die Liste der gewählten Phase, die wartendsten zuerst, mit
 * Hinweisen, wo etwas hängt („Nachfassen“, „Leistungsdatum fehlt“, „Beginn überschritten“), seitenweise auch bei Hunderten. Die Phasen zeigen nur, was die Rolle in Anfragen,
 * Angeboten und Aufträgen auch sonst sehen darf.
 */
export function PipelinePage() {
  const [params, setParams] = useSearchParams();
  const stageParam = params.get('phase');
  const stage = (PIPELINE_STAGES as readonly string[]).includes(stageParam ?? '') ? (stageParam as PipelineStage) : undefined;
  const page = Math.max(1, Number(params.get('seite')) || 1);
  const [input, setInput] = useState('');
  const [q, setQ] = useState('');
  // Erst nach kurzer Pause suchen, damit nicht jeder Buchstabe eine Abfrage auslöst
  useEffect(() => {
    const t = setTimeout(() => setQ(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);

  const board = useQuery({
    queryKey: ['order-pipeline', q, stage ?? '', page],
    queryFn: async () => (await api<{ data: PipelineBoard }>('GET', `/orders/pipeline${queryString({ q: q || undefined, stage, page })}`)).data,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });
  const data = board.data;
  const total = data?.columns.reduce((n, c) => n + c.total, 0) ?? 0;
  const selected = data?.selected ?? stage ?? 'inquiry';
  const column = data?.columns.find((c) => c.stage === selected);
  const hasPrices = column?.cards.some((c) => c.netCents !== null) ?? false;
  const select = (next: PipelineStage) => setParams({ phase: next });
  const go = (next: number) => setParams({ phase: selected, seite: String(next) });

  return (
    <>
      <div className="page-head">
        <h1>Auftragsübersicht</h1>
        <span className="muted">Von der Anfrage bis zur Rechnung</span>
        <Link to="/auftraege/neu" className="btn btn--primary page-head__action">Neuer Auftrag</Link>
      </div>
      <div className="cockpit-search">
        <label className="sr-only" htmlFor="cockpit-q">Suchen</label>
        <input id="cockpit-q" type="search" className="input" placeholder="Nummer, Betreff oder Name suchen" value={input} onChange={(e) => setInput(e.target.value)} />
        {data && <span className="muted small">{total} {q ? 'Treffer' : 'Einträge'} in allen Phasen</span>}
      </div>
      {board.isError && <div className="alert alert--error" role="alert">{(board.error as Error).message}</div>}
      {board.isLoading && <p className="muted">Wird geladen …</p>}
      {data && total === 0 && <div className="card"><p className="muted">{q ? `Nichts gefunden für „${q}“.` : 'Noch nichts im Ablauf. Sobald Anfragen, Angebote oder Aufträge da sind, erscheinen sie hier in ihrer Phase.'}</p></div>}
      {data && total > 0 && (
        <>
          <div className="phases" role="group" aria-label="Phasen">
            {data.columns.map((c, i) => (
              <button
                key={c.stage}
                type="button"
                className={`phase${c.stage === selected ? ' is-selected' : ''}${c.total === 0 ? ' is-empty' : ''}`}
                style={{ '--stage': STAGE_COLORS[i % STAGE_COLORS.length] } as React.CSSProperties}
                aria-pressed={c.stage === selected}
                onClick={() => select(c.stage)}
              >
                <span className="phase__name">{SHORT[c.stage]}</span>
                <span className="phase__count">{c.total}</span>
                <span className="phase__net">{c.netCents !== null && c.netCents > 0 ? formatCents(c.netCents) : ' '}</span>
              </button>
            ))}
          </div>
          {column && (
            <section className="card phase-panel" aria-label={PIPELINE_STAGE_LABELS[selected]} style={{ '--stage': STAGE_COLORS[PIPELINE_STAGES.indexOf(selected) % STAGE_COLORS.length] } as React.CSSProperties}>
              <div className="phase-panel__head">
                <div>
                  <h2>{PIPELINE_STAGE_LABELS[selected]}</h2>
                  <p className="muted small">{PIPELINE_STAGE_HINTS[selected]} Die am längsten wartenden stehen oben.</p>
                </div>
                <Link to={`${listLink[selected]}${q ? `${listLink[selected].includes('?') ? '&' : '?'}q=${encodeURIComponent(q)}` : ''}`} className="btn btn--secondary btn--sm">In der Liste öffnen</Link>
              </div>
              {column.cards.length === 0 ? (
                <p className="muted phase-panel__empty">Nichts in dieser Phase.</p>
              ) : (
                <table className="table table--cards phase-table">
                  <thead>
                    <tr><th>Nummer</th><th>Betreff und Kunde</th><th>Hinweise</th><th>Datum</th>{hasPrices && <th className="num">Netto</th>}</tr>
                  </thead>
                  <tbody>
                    {column.cards.map((card) => (
                      <tr key={`${card.kind}-${card.id}`}>
                        <td data-label="Nummer"><Link to={cardLink(card)}><strong>{card.title}</strong></Link></td>
                        <td data-label="Betreff und Kunde">{card.subtitle}</td>
                        <td data-label="Hinweise">{card.badges.length > 0 ? <span className="cockpit-badges">{card.badges.map((b) => <span key={b.label} className={`cockpit-badge cockpit-badge--${b.tone}`}>{b.label}</span>)}</span> : <span className="muted">–</span>}</td>
                        <td data-label="Datum">{day(card.date)}</td>
                        {hasPrices && <td data-label="Netto" className="num">{card.netCents !== null ? formatCents(card.netCents) : '–'}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {data.lastPage > 1 && (
                <nav className="phase-pager" aria-label="Seiten">
                  <button type="button" className="btn btn--secondary btn--sm" disabled={data.page <= 1} onClick={() => go(data.page - 1)}>← Zurück</button>
                  <span className="muted small">Seite {data.page} von {data.lastPage}, {column.total} Einträge</span>
                  <button type="button" className="btn btn--secondary btn--sm" disabled={data.page >= data.lastPage} onClick={() => go(data.page + 1)}>Weiter →</button>
                </nav>
              )}
            </section>
          )}
        </>
      )}
    </>
  );
}
